import React, { useMemo } from 'react';
import { Monitor, Smartphone, ZoomIn, ZoomOut } from 'lucide-react';
import { usePatternStudio } from '../context/PatternStudioContext';
import { PatternPages, PAGE_W, PAGE_H } from './PreviewDocument';

const ZOOM_LEVELS = [0.4, 0.55, 0.7, 0.85, 1.0];

const THUMB_W = 72;
const THUMB_H = Math.round((PAGE_H / PAGE_W) * THUMB_W); // ≈ 102
const THUMB_SCALE = THUMB_W / PAGE_W; // ≈ 0.121

export default function PreviewPane() {
  const { state, dispatch } = usePatternStudio();
  const { pattern, previewPage, previewMode, previewZoom } = state;

  const pages = useMemo(
    () => PatternPages({ pattern, photos: pattern.photos, branding: pattern.branding }) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(pattern)]
  );

  const totalPages = pages.length || 1;
  const clampedPage = Math.min(previewPage, totalPages - 1);

  function zoomIn() {
    const next = ZOOM_LEVELS.find(z => z > previewZoom);
    if (next) dispatch({ type: 'SET_PREVIEW_ZOOM', zoom: next });
  }
  function zoomOut() {
    const prev = [...ZOOM_LEVELS].reverse().find(z => z < previewZoom);
    if (prev) dispatch({ type: 'SET_PREVIEW_ZOOM', zoom: prev });
  }

  const scaledW = PAGE_W * previewZoom;
  const scaledH = PAGE_H * previewZoom;

  return (
    <div className="flex flex-col h-full" style={{ background: '#1C1C1C' }}>
      {/* Toolbar */}
      <div
        className="flex items-center gap-2 px-3 py-2 flex-shrink-0"
        style={{ background: '#2A2A2A', borderBottom: '1px solid #3A3A3A' }}
      >
        <span className="text-xs font-semibold" style={{ color: '#9CA3AF', marginRight: 2 }}>Preview</span>

        {/* Mode toggle */}
        <button
          onClick={() => dispatch({ type: 'SET_PREVIEW_MODE', mode: 'desktop' })}
          className="p-1.5 rounded-lg transition-all"
          style={previewMode === 'desktop' ? { background: '#3A3A3A', color: '#E5E7EB' } : { color: '#6B7280' }}
          title="Desktop mode"
        >
          <Monitor size={13} />
        </button>
        <button
          onClick={() => dispatch({ type: 'SET_PREVIEW_MODE', mode: 'mobile' })}
          className="p-1.5 rounded-lg transition-all"
          style={previewMode === 'mobile' ? { background: '#3A3A3A', color: '#E5E7EB' } : { color: '#6B7280' }}
          title="Mobile mode"
        >
          <Smartphone size={13} />
        </button>

        <div className="flex-1" />

        {/* Zoom controls */}
        <button
          onClick={zoomOut}
          className="p-1 transition-all"
          style={{ color: previewZoom <= ZOOM_LEVELS[0] ? '#4B5563' : '#9CA3AF' }}
          disabled={previewZoom <= ZOOM_LEVELS[0]}
        >
          <ZoomOut size={13} />
        </button>
        <span className="text-xs min-w-8 text-center" style={{ color: '#9CA3AF' }}>
          {Math.round(previewZoom * 100)}%
        </span>
        <button
          onClick={zoomIn}
          className="p-1 transition-all"
          style={{ color: previewZoom >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1] ? '#4B5563' : '#9CA3AF' }}
          disabled={previewZoom >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
        >
          <ZoomIn size={13} />
        </button>
      </div>

      {/* Body: thumbnail strip + main canvas */}
      <div className="flex flex-1 min-h-0">

        {/* Canva-style page thumbnail strip */}
        {pages.length > 0 && (
          <div
            className="flex flex-col items-center overflow-y-auto py-3 gap-3 flex-shrink-0"
            style={{ width: 84, background: '#242424', borderRight: '1px solid #333' }}
          >
            {pages.map((page, i) => (
              <button
                key={i}
                onClick={() => dispatch({ type: 'SET_PREVIEW_PAGE', page: i })}
                className="flex flex-col items-center gap-1.5 flex-shrink-0 group"
                title={`Page ${i + 1}`}
              >
                {/* Thumbnail frame */}
                <div
                  style={{
                    width: THUMB_W,
                    height: THUMB_H,
                    overflow: 'hidden',
                    borderRadius: 3,
                    border: `2px solid ${i === clampedPage ? '#C4A882' : 'transparent'}`,
                    boxShadow: i === clampedPage
                      ? '0 0 0 1px #C4A882, 0 2px 8px rgba(0,0,0,0.5)'
                      : '0 1px 4px rgba(0,0,0,0.5)',
                    background: '#fff',
                    position: 'relative',
                    flexShrink: 0,
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                >
                  {/* Scaled-down page render */}
                  <div
                    style={{
                      transform: `scale(${THUMB_SCALE})`,
                      transformOrigin: 'top left',
                      width: PAGE_W,
                      height: PAGE_H,
                      pointerEvents: 'none',
                      userSelect: 'none',
                    }}
                  >
                    {page}
                  </div>
                </div>
                {/* Page number */}
                <span
                  style={{
                    fontSize: 9,
                    lineHeight: 1,
                    color: i === clampedPage ? '#C4A882' : '#6B7280',
                    transition: 'color 0.15s',
                  }}
                >
                  {i + 1}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Main preview canvas */}
        <div className="flex-1 overflow-auto flex items-start justify-center p-5">
          {pages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div
                className="w-16 h-20 rounded-xl flex items-center justify-center mb-3"
                style={{ border: '2px dashed #4B5563' }}
              >
                <span className="text-2xl" style={{ opacity: 0.3 }}>✂️</span>
              </div>
              <p className="text-xs" style={{ color: '#6B7280' }}>
                Fill in pattern details<br />to see a live preview
              </p>
            </div>
          ) : (
            <div
              style={{
                width: scaledW,
                height: scaledH,
                overflow: 'hidden',
                boxShadow: '0 8px 48px rgba(0,0,0,0.55)',
                borderRadius: 4,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  transform: `scale(${previewZoom})`,
                  transformOrigin: 'top left',
                  width: PAGE_W,
                  height: PAGE_H,
                }}
              >
                {pages[clampedPage] || pages[0]}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Page counter footer */}
      {totalPages > 1 && (
        <div
          className="flex items-center justify-center py-1.5 flex-shrink-0"
          style={{ background: '#2A2A2A', borderTop: '1px solid #333' }}
        >
          <span className="text-xs" style={{ color: '#6B7280' }}>
            {clampedPage + 1} of {totalPages} pages
          </span>
        </div>
      )}
    </div>
  );
}
